import { APP_BASE_HREF } from '@angular/common';
import {
    Component,
    forwardRef,
    OnChanges,
    KeyValueDiffers,
    Inject,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    ElementRef,
    ViewChild,
    KeyValueDiffer,
    Input,
    Injector
} from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as am5 from '@amcharts/amcharts5';
import { catchError, combineLatest, debounceTime, filter, map, merge, Observable, of, Subscription, switchMap, take, tap, debounce, timer } from 'rxjs';
import * as htmlToImage from 'html-to-image';
import { isEqual, isNil } from 'lodash';
import { MessageService } from 'primeng/api';

import { BlockComponent } from '@Common/PageBuilder/components/Block/block.component';
import { ChartDisplayElementModel, ChartValidSourceStateEnum } from '@Common/Reports/models/chart-display-element.model';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { ChartModel } from '@Common/Charts/models/chart.model';
import { ReportBlockTypeEnum, reportBlockTypesToChartAccentColorMapkeysMap } from '@Common/Reports/enums/report-block-type.enum';
import { ReportsSeriesPreviewService } from '@Common/Reports/services/report-series-preview.service';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import { ReportsChartOptionsService } from '@Common/Reports/services/reports-chart-options.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { GeographyTypesEnum } from '../../../../api/journodex/enum/geography-types.enum';
import { ChartMetadataModel } from '@Common/Charts/models/chart-metadata.model';
import { MapChartModel } from '@Common/Charts/models/map-chart.model';
import { ReportDateRangeModel } from '@Common/Reports/models/report-date-range.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CategoryValueXYChartOptions } from '@Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../../api/externalData/enums/google-analytics-report-types.enum';
import { SeriesTypeEnum } from '@Common/Reports/enums/series-type.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { ProminenceLockerService } from '@Common/Misc/services/prominence-locker.service';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { HeatChartModel } from '@Common/Charts/models/heatChart/heat-chart.model';
import {
    ExternalChartMetricsEnum,
    externalChartMetricToTopReferralSourcesReportRequestTypeMap
} from '@Common/Reports/enums/external-chart-metrics.enum';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import {
    UserAppPreferenceChartColors
} from '@Core/Users/models/user-app-preferences-chart-colors.model';
import { ClickOutsideService } from '@Common/PageBuilder/services/click-outside.service';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportsChartDetailService } from '@Common/Reports/services/reports-chart-detail.service';
import { WordCloudChartModel } from '@Common/Charts/models/WordCloudChart/word-cloud-chart.model';
import { WordCloudSeriesModel } from '@Common/Charts/models/WordCloudChart/word-cloud-series.model';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '@Core/Config/services/config.service';
import { AbstractChartOptionsModel } from '@Common/Charts/models/abstract-chart-options.model';
import { ActionKeys } from '@Common/Forms/enums/keys.enum';
import { DataConnectionService } from '@Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { DateValueXYChart } from '@Common/Charts/models/date-value-x-y-chart.model';
import { CategoryValueXYChart } from '@Common/Charts/models/category-value-x-y-chart.model';
import { stackedBarColorsConfig } from '../../../../Common/Charts/config/stacked-bar-colors.config';
import { MediaTypeEnum, mediaTypeColorKeyMap } from '@Common/Basic/enums/media-type.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { PieChart } from '@Common/Charts/models/pieChart/pie-chart-new.model';
import { ReportsSeriesPreviewNewService } from '@Common/Reports/services/reports-series-preview-new.service';
import { ChartSpecificTypesEnum } from '@Common/Reports/enums/chart-specific-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { SeriesModel } from '@Common/Charts/models/series.model';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { DataListChartOptionsModel } from '@Common/Charts/models/data-list-chart-options.model';
import { DataListChartComponent } from '@Common/Charts/components/DataListChart/data-list-chart.component';
import { BlockDisplayTypeEnum } from '../../../../Common/PageBuilder/enums/block-display-type.enum';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { ColorModel } from '@Common/Forms/models/color.model';
import { CoverageMetricChartDisplayElement } from '@Common/Reports/models/coverage-metric-chart-display-element.model';
import { BlockDisplayElementChangedEvent } from '@Common/PageBuilder/models/block-display-element-changed-event.model';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { Am5ChartConfigurationService } from '@Common/Reports/services/am5-chart-configuration.service';
import { Am5DateValueXYChart } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5ChartSettingsService } from '@Common/Reports/services/am5-chart-settings.service';
import { Am5ChartTooltipService } from '@Common/Charts/services/am5-chart-tooltip.service';
import { am5BlockTypes } from '@Common/Reports/models/new-data-processing-workflow-block-types.model';
import { ChartAnnotationDialogComponent } from '@Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-dialog.component';
import { ChartAnnotationMarkerTypesEnum } from '@Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { ChartAnnotations } from '@Common/Reports/models/chart-annotations.model';
import {
    canvasBlockTypesToAnnotationAdjustMap, emailBriefingsBlockTypesToAnnotationAdjustMap,
    canvasBlockTypesToInDialogAnnotationAdjustMap, emailBriefingsBlockTypesToInDialogAnnotationAdjustMap
} from '@Common/Reports/maps/block-types-to-annotation-adjust.map';
import { Am5DateValueXYChartConfiguration } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-configuration.model';
import { Am5ChartConfiguration } from '@Common/Charts/models/am5-chart-configuration.model';
import { Am5BriefingChartComponent } from '../Am5BriefingChart/am5-briefing-chart.component';
import { CanvasChartComponent } from '@Common/Charts/components/CanvasChart/canvas-chart.component';
import { Am5DateValueXYChartSettings } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-settings.model';
import { Am5ChartSettings } from '@Common/Charts/models/am5-chart-settings.model';
import { Am5CategoryValueXYBarChart } from '@Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYBarChart/am5-category-value-x-y-bar-chart.model';
import { Am5CategoryValueXYColumnChart } from '@Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYColumnChart/am5-category-value-x-y-column-chart.model';
import { Am5CategoryValueXYBaseChartConfiguration } from '@Common/Charts/models/Am5CategoryValueXYChart/am5-category-value-x-y-base-chart-configuration.model';

/**
 * This component is a mirror of ChartBlockComponent, which felicitates the construction of the
 * chart model. This is used in the BriefingChartComponent for am4chart rendering & chart image generation.
 * Preview is not supported, since this component is only used in the briefing chart block.
 */
@Component({
    selector: 'agpr-briefing-chart-block',
    templateUrl: './briefing-chart-block.component.html',
    styleUrls: ['./briefing-chart-block.component.scss'],
    standalone: false,
    providers: [
        Am5ChartTooltipService,
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => BriefingChartBlockComponent)
        }
    ]
})
export class BriefingChartBlockComponent extends BlockComponent implements OnInit, OnChanges, OnDestroy {
    @Input() actionMenuTextAlignment: string;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly reportBlockTypeEnum = ReportBlockTypeEnum;
    readonly prominenceStatuses = ProminenceRecalculationJobStatus;
    readonly blockDisplayTypeEnum = BlockDisplayTypeEnum;
    readonly chartSpecificTypesEnum = ChartSpecificTypesEnum;
    readonly entityTypes = EntityType;
    readonly am5BlockTypes = am5BlockTypes;

    @ViewChild('blockContent') blockContentContainer: ElementRef;
    @ViewChild('coverageSummaryChart') coverageSummaryChart: ElementRef;
    @ViewChild('topTermChart') topTermChart: ElementRef;
    @ViewChild('coverageMetricChart') coverageMetricChart: ElementRef;
    @ViewChild('amCanvasChart') amCanvasChart: CanvasChartComponent;
    @ViewChild('am5EmailBriefingChart') am5EmailBriefingChart: Am5BriefingChartComponent;

    @Output() outputEvent = new EventEmitter<any>();
    @ViewChild('dataListChart') dataListChart: DataListChartComponent;

    chart: ChartModel;
    am5Chart: Am5Chart;
    reportSeries: ReportSeriesModel[];
    loading: boolean = false;
    isCanvas: boolean = false;

    chartDisplayElement: ChartDisplayElementModel;
    lastChartDisplayElement: ChartDisplayElementModel;

    blockDisplayElement: BriefingChartDisplayElementModel;

    briefingChartDisplayElement: BriefingChartDisplayElementModel;
    lastBriefingChartDisplayElement: BriefingChartDisplayElementModel;


    protected userChartColors$: Observable<UserAppPreferenceChartColors>;
    private userChartColors: UserAppPreferenceChartColors;

    prominenceStatus: ProminenceRecalculationJobStatus;

    isProminenceChart: boolean = false;
    hasProminenceProvision: boolean = false;
    noProminenceContactLink: string;
    prominenceStatusSubscription: Subscription;
    chartOptions: AbstractChartOptionsModel = {};
    missingDataSource: boolean = false;
    noData: boolean = false;
    originalData: any;
    dataRefreshed: boolean = false;
    chartContentWidth: number;
    chartContentHeight: number;
    hasAnnotation: boolean = false;
    plotTop: number = 0;
    plotBottom: number = 0;
    plotLeft: number = 0;
    annotationTopAdjust: number = 0;
    annotationBottomAdjust: number = 0;
    inDialogAnnotationTopAdjust: number = 0;
    inDialogAnnotationBottomAdjust: number = 0;

    nonAmChartPadding: string;
    nonAmChartSeries: SeriesModel[] = [];
    dataListChartOptions: DataListChartOptionsModel;

    imageString: string;
    blockWidth: number;
    oldImageWidthPx: number;
    blockHeight: number;
    oldImageHeightPx: number;
    oldAccentColor: string;
    briefingChartModelDiffer: KeyValueDiffer<string, any>;
    barColor: string = chartColors.agprBlue;
    topTermSmall: boolean = false;
    saving: boolean = false;
    skinny: boolean = false;

    private dataProviderSubscriptions: any;

    // Tracks whether we've already reconstructed the chart for the currently-pending
    // refresh request - see isGenuineDataRefresh() for why this is needed.
    private reconstructedForPendingRefresh = false;

    private dataArrivalSubscription: Subscription;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public prominenceLockerService: ProminenceLockerService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public el: ElementRef,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
        public coreDialogService: CoreDialogService,
        private injector: Injector,
        public messageService: MessageService,
    ) {
        super(differs, pageBuilderService, changeDetectorRef, clickOutsideService, el);

        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(colors => {
                this.chartOptions.defaultColors = colors.generalColors;
                this.userChartColors = colors;
                this.setLabels();
            }),
            this.userStateService.getUserCurrency().subscribe(currency => this.chartOptions.currencyCode = currency)
        );
    }


    ngOnInit(): void {
        super.ngOnInit();
        this.briefingChartModelDiffer = this.differs.find((this.block.displayElement as BriefingChartDisplayElementModel).chart).create();
        this.dataListChartOptions = this.reportsChartOptionsService.getDataListChartOptions();
        this.userChartColors$ = this.userPreferencesService.getUserAppPreferences().pipe(
            map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
            catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
        );

        this.subscriptions.push(
            this.monitorConfigService?.getProminenceProvision().subscribe(groupsLimit => {
                this.hasProminenceProvision = groupsLimit > 0;
            }),
            this.userChartColors$.subscribe(userChartColors => {
                this.userChartColors = userChartColors;
            }),
            this.pageBuilderService.getSaving().subscribe(saving => {
                this.saving = saving;
            }),
            this.pageBuilderService.getBlocks().subscribe(blocks => {
                if (this.isInLayoutBlock || this.isInCuratorBlock) {
                    this.setContextMenuEditingOptions();
                }
            }),
            this.subscribeToBlockChanges().subscribe()
        );
    }

    subscribeToBlockChanges(): Observable<BlockDisplayElementChangedEvent> {
        return this.pageBuilderService.getBlockDisplayElementChanged().pipe(
            debounce((event) => event.block === this.block ? of(null) : timer(300)),
            tap(event => {
                if (
                    !this.renderable ||
                    event.block === this.block ||
                    (event.stylesOnly && event.block.type.key === this.block.type.key)
                ) {
                    this.ngOnChanges();
                    this.changeDetectorRef.detectChanges();
                }
            })
        );
    }

    ngOnChanges() {
        this.blockDisplayElement = this.block.displayElement as BriefingChartDisplayElementModel;

        if (this.blockDisplayElement.chartBlock) {
            this.chartDisplayElement = this.blockDisplayElement.chartBlock.displayElement as ChartDisplayElementModel;
            const type: ReportBlockTypeEnum = this.blockDisplayElement.chartBlock.type.key as ReportBlockTypeEnum;

            const validSourceState = this.chartDisplayElement?.checkValidSourcesForChartType(type);
            this.renderable = validSourceState !== ChartValidSourceStateEnum.NONE;
            this.missingDataSource = validSourceState === ChartValidSourceStateEnum.SOME;
            this.isProminenceChart = type === ReportBlockTypeEnum.PROMINENCE_BY_REACH
                || type === ReportBlockTypeEnum.PROMINENCE_OVER_TIME;
        } else {
            this.renderable = false;
            this.missingDataSource = false;
            this.isProminenceChart = false;
        }

        if (this.isProminenceChart && !this.prominenceStatusSubscription) {
            this.prominenceStatusSubscription = this.prominenceLockerService.prominenceStatus$.subscribe(status => {
                const statusChanged = status !== this.prominenceStatus;
                this.prominenceStatus = status;
                if (statusChanged) {
                    this.updateChart();
                }
            });
            this.subscriptions.push(this.prominenceStatusSubscription);
        }

        if (this.renderable) {
            this.updateChart();

            if (!this.dataProviderSubscriptions) {
                this.dataProviderSubscriptions = (
                    (this.block.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel
                ).series.map((seriesItem) => {
                    return seriesItem.dataProvider;
                });

                combineLatest(this.dataProviderSubscriptions).pipe(debounceTime(300)).subscribe({
                    next: (data) => {
                        this.dataRefreshed = this.isGenuineDataRefresh(data);
                        this.loading = this.dataRefreshed;
                        if (this.dataRefreshed) {
                            this.updateChart();
                            this.pageBuilderService.setIncrementRefreshAllProgressBar(this.block);
                        }
                        this.originalData = data;
                        this.changeDetectorRef.detectChanges();
                    },
                    error: () => {
                        this.loading = false;

                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'PageBuilder.blockType.chart.retrievingChartData.error.heading',
                            detail: 'PageBuilder.blockType.chart.retrievingChartData.error.message'
                        });

                        if (this.blockDisplayElement.chart.refreshed) {
                            this.pageBuilderService.setIncrementRefreshAllProgressBar(this.block);
                        }
                    }
                });
            }
        }

        this.setNoProminenceContactLink();
    }


    // Ignores the dataProvider echo that reconstructing for a refresh itself causes, so it doesn't reconstruct again in a loop
    private isGenuineDataRefresh(data: any): boolean {
        if (!this.originalData) {
            return false;
        }
        const refreshRequested = this.blockDisplayElement.chart.refreshed;
        const isNewRefresh = refreshRequested && !this.reconstructedForPendingRefresh;
        const refreshed = isNewRefresh || !isEqual(data, this.originalData);
        this.reconstructedForPendingRefresh = refreshRequested && (this.reconstructedForPendingRefresh || refreshed);
        return refreshed;
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.dataArrivalSubscription?.unsubscribe();
    }

    updateChart() {
        const block: BlockModel = this.blockDisplayElement.chartBlock;
        const key = block.type.key as ReportBlockTypeEnum;

        const columnWidth = this.pageBuilderService.getColumnWidth(this._blockPosition);
        const imageWidthPx = this.getImageWidth(columnWidth, this.blockDisplayElement);
        this.chartContentWidth = imageWidthPx;
        const imageHeightPx = this.blockDisplayElement.chart.heightPx;
        const accentColor = this.blockDisplayElement.styles['accentColor'];
        if (!this.oldImageWidthPx) {
            this.oldImageWidthPx = this.blockDisplayElement.chart.oldWidthPx;
        }
        if (!this.oldImageHeightPx) {
            this.oldImageHeightPx = this.blockDisplayElement.chart.oldHeightPx;
        }
        if (imageHeightPx < 100) {
            return;
        }

        if (this.saving) {
            return;
        }

        const widthChanged = imageWidthPx !== this.oldImageWidthPx;
        const heightChanged = imageHeightPx !== this.oldImageHeightPx;

        const oldChartBlockEqual = !this.blockDisplayElement.oldChartBlock || this.blockDisplayElement.chartBlock?.equals(this.blockDisplayElement.oldChartBlock);
        const displayElementEqual = !this.lastChartDisplayElement || this.chartDisplayElement?.isEqual(this.lastChartDisplayElement);
        if (oldChartBlockEqual
            && displayElementEqual
            && (this.chart || this.am5Chart || this.blockDisplayElement.chart.chartUrl)
            && Math.round(imageWidthPx) === Math.round(this.oldImageWidthPx)
            && imageHeightPx === this.oldImageHeightPx
            && accentColor === this.oldAccentColor
            && !this.dataRefreshed
        ) {
            // chart options have changed, but the image has not, update only the twig
            if (!this.loading) {
                this.updateTwigData();
            }
            return;
        }
        this.dataRefreshed = false;
        this.isCanvas = this.block.displayElement.displayType === BlockDisplayTypeEnum.ONLINE;

        this.blockWidth = imageWidthPx;
        this.blockHeight = this.skinny ? imageHeightPx + 30 : imageHeightPx;

        if (this.isCanvas) {
            const data: any = {};
            this.twigData = Object.assign(data, this.block.displayElement, this.block.displayElement.settings);
            this.twigData.widthPercent = this.blockDisplayElement.chart.widthPercent;

            let paddingTop = this.blockDisplayElement?.styles?.padding?.paddingAll || 0;
            let paddingBottom = this.blockDisplayElement?.styles?.padding?.paddingAll || 0;
            if (this.blockDisplayElement?.styles?.padding?.paddingOption) {
                paddingTop = this.blockDisplayElement?.styles?.padding?.leftPadding || 0;
                paddingBottom = this.blockDisplayElement?.styles?.padding?.rightPadding || 0;
            }

            this.twigData.displaySize = imageHeightPx;
        }

        const series = this.chartDisplayElement?.series?.filter(seriesItem => {
            if (seriesItem._chartType === ReportBlockTypeEnum.COVERAGE_METRIC && (this.chartDisplayElement?.metric === ChartMetricsEnum.custom)) {
                if (isNil((this.chartDisplayElement as CoverageMetricChartDisplayElement)?.customCurrentValue)) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return seriesItem.isValid();
            }
        });

        if (series?.length < 1) {
            this.chart = null;
            this.lastChartDisplayElement = null;
            return;
        }

        this.oldImageWidthPx = imageWidthPx;
        this.blockDisplayElement.chart.widthPx = imageWidthPx;
        this.oldImageHeightPx = imageHeightPx;
        this.oldAccentColor = accentColor;

        const padding = this.blockDisplayElement.styles.padding;
        const paddingWidth = (padding?.paddingOption ? padding.leftPadding + padding.rightPadding : padding?.paddingAll * 2) || 0;
        this.chartContentWidth = (this.pageBuilderService.getColumnWidth(this.blockPosition) - paddingWidth) *
            this.blockDisplayElement.chart.widthPercent / 100 || null;
        const paddingHeight = (padding?.paddingOption ? padding.topPadding + padding.bottomPadding : padding?.paddingAll * 2) || 0;
        this.chartContentHeight = this.blockDisplayElement.chart.heightPx - paddingHeight;

        if (this.chartDisplayElement?.styles?.colorMap?.barColor) {
            this.barColor = this.chartDisplayElement.styles.colorMap.barColor;
        } else {
            this.barColor = chartColors.agprBlue;
        }
        if (accentColor) {
            this.applyAccentColor(accentColor, key);
        }

        if (this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.CARD_LIST && (imageHeightPx < this.chartDisplayElement.numOfItems * 90)) {
            this.topTermSmall = true;
        } else {
            this.topTermSmall = false;
        }
        if (this.chartOptions.currencyCode && this.chartDisplayElement?.styles) {
            this.chartDisplayElement.styles.currency = this.chartOptions.currencyCode;
        }

        series.forEach(_series => {
            _series.metric = this.chartDisplayElement.metric;
            _series.metrics = this.chartDisplayElement.metrics;
            _series.termType = this.chartDisplayElement.termType;
            _series.numOfItems = this.chartDisplayElement.numOfItems;
            _series.hasPreviousPeriod = this.chartDisplayElement.styles.chartDisplaySettings.previousPeriod;
            _series.chartSpecificType = this.chartDisplayElement.chartSpecificType;
        });

        // cache the display element so we can verify if we actually need to refresh the chart
        this.lastChartDisplayElement = this.chartDisplayElement?.clone();
        this.setLabels();

        // these checks need to occur before the check for this.preview, and that check has to happen
        // before the switch statement
        // these mutations affect what the preview series looks like, so need to happen first
        switch (key) {
            case ReportBlockTypeEnum.COVERAGE_SUMMARY: {
                this.skinny = columnWidth < 240;
                this.nonAmChartSeries = series.map(originalSeries => {
                    const newSeries: SeriesModel = originalSeries.clone();

                    const removeInvalidMetrics: Function = (value: DataPointModel[]): DataPointModel[] => {
                        const selectedOption: string[] = this.chartDisplayElement.metrics ?? [
                            ChartMetricsEnum.volumeOfMentions,
                            ChartMetricsEnum.audienceReach,
                            ChartMetricsEnum.ave
                        ];

                        if (value?.length && selectedOption?.length) {
                            value = value
                                .filter(x => selectedOption.indexOf(x.id) !== -1)
                                .sort((a, b) => selectedOption.indexOf(a.id) - selectedOption.indexOf(b.id));
                        }

                        return value;
                    };

                    newSeries.dataProvider = originalSeries.dataProvider.pipe(map((value) => removeInvalidMetrics(value)));
                    newSeries.previousPeriodDataProvider = originalSeries.previousPeriodDataProvider.pipe(map((value) => removeInvalidMetrics(value)));

                    return newSeries as ReportSeriesModel;
                }) || [];
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                series[0].subSeries = null;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                series.forEach(_series => {
                    _series.sentiments = this.chartDisplayElement.sentiments;
                });
                break;
            case ReportBlockTypeEnum.REFERRAL_TREND:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                series.forEach(seriesModel => {
                    if (seriesModel.type === SeriesTypeEnum.googleAnalytics) {
                        seriesModel.referralTypes = this.chartDisplayElement.referralTypes;
                        seriesModel.subSeries = seriesModel.subSeries.filter(
                            subSeries => seriesModel.referralTypes.includes(subSeries.key as GoogleAnalyticsReportTypesEnum)
                        );
                    } else {
                        seriesModel.referralTypes = [];
                        seriesModel.subSeries = [];
                    }
                    seriesModel.subSeries?.forEach(subseries => {
                        subseries.label = this.localizationService.transform(subseries.label);
                    });
                });
                break;
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                const newRequestType = externalChartMetricToTopReferralSourcesReportRequestTypeMap
                    .get(this.chartDisplayElement.metric as ExternalChartMetricsEnum);
                series.forEach(_series => {
                    if (_series.type === SeriesTypeEnum.googleAnalytics) {
                        _series.externalData.googleAnalyticsReportType = newRequestType;
                        _series.metric = this.chartDisplayElement.metric;
                    }
                });
                break;
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                series.forEach(_series => {
                    _series.styles.networkSelection = this.chartDisplayElement.networkSelection;
                });
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                if (series.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId)) {
                    series.forEach((_series, index) => {
                        const colorMap = this.chartDisplayElement.styles?.colorMap;
                        if (colorMap) {
                            _series.color = colorMap['subSource' + (index + 1)] || colorMap['seriesColor' + index];
                        }
                        _series.color = _series.color || this.chartOptions.defaultColors?.['color' + (index + 1)];
                    });
                }
                break;
            case ReportBlockTypeEnum.TOP_POSTS:
            case ReportBlockTypeEnum.TOP_MENTIONS: {
                // TODO not support in first release of CIB
                break;
            }
        }
        const liveChartAdjustMap = this.isCanvas ? canvasBlockTypesToAnnotationAdjustMap : emailBriefingsBlockTypesToAnnotationAdjustMap;
        const adjusts = liveChartAdjustMap.get(
            this.blockDisplayElement.chartBlock.type.key as ReportBlockTypeEnum
        );
        if (adjusts) {
            [this.annotationTopAdjust, this.annotationBottomAdjust] = adjusts;
        }
        const inDialogAdjusts = (this.isCanvas ? canvasBlockTypesToInDialogAnnotationAdjustMap : emailBriefingsBlockTypesToInDialogAnnotationAdjustMap).get(
            this.blockDisplayElement.chartBlock.type.key as ReportBlockTypeEnum
        );
        if (inDialogAdjusts) {
            [this.inDialogAnnotationTopAdjust, this.inDialogAnnotationBottomAdjust] = inDialogAdjusts;
        }

        // check if the data has arrived
        this.dataArrivalSubscription?.unsubscribe();
        const usesSeriesData = !(key === ReportBlockTypeEnum.COVERAGE_METRIC && this.chartDisplayElement.metric === ChartMetricsEnum.custom);
        if (usesSeriesData && !this.hasDataArrived(series)) {
            this.loading = this.block.displayElement.displayType !== BlockDisplayTypeEnum.ONLINE;
            this.waitForData(series);
            return;
        }

        let chartSeries, chartConfiguration, chartSettings;
        switch (key) {
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON: {
                const displayElement = block.displayElement as ChartDisplayElementModel;
                this.setColorMapDefaultValues();
                const chartSpecificType = displayElement.chartSpecificType;
                const useSubSourceColorMap = [ReportBlockTypeEnum.SHARE_OF_VOICE, ReportBlockTypeEnum.COVERAGE_COMPARISON].includes(key)
                        && series.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId);
                if (chartSpecificType === ChartSpecificTypesEnum.PIE) {
                    const options = this.reportsChartOptionsService.getPieChartOptions(this.chartDisplayElement);
                    options.noPadding = true;
                    options.maxNumberChartItems = 7;
                    options.restrictChartItemsToMaxNumber = true;
                    if (useSubSourceColorMap) {
                        options.seriesColorMapKey = 'subSource';
                    }
                    this.chart = new PieChart(
                        series,
                        options,
                        this.getChartMetadata(),
                        this.reportsChartSeriesConverterService.getChartSeries(series, block)
                    );
                } else {
                    const options = this.reportsChartOptionsService.getOriginalPieChartBarColumnVisualizationOptions(this.chartDisplayElement, key);
                    options.noPadding = true;
                    options.showFewerItemsAtLimitedSpace = false;
                    options.styles['legendItemWidth'] = key === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN || key === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN
                        ? 110 : 150;
                    options.legendScrollDisabled = true;
                    const chartMetadata = this.getChartMetadata();
                    const xySeries = this.reportsChartSeriesConverterService.getChartSeries(series, block);
                    if (
                        key === ReportBlockTypeEnum.SHARE_OF_VOICE
                        || key === ReportBlockTypeEnum.COVERAGE_COMPARISON
                    ) {
                        options.useReportSeriesColor = true;
                        options.sharedReportChartMinHeight = 450;
                    }
                    if (key === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK) {
                        options.imageAsCategory = !options.stacked;
                        options.appBaseHref = this.appBaseHref;
                        if (this.chartDisplayElement.metric === ChartMetricsEnum.ENGAGEMENT_RATE) {
                            options.symbol = '%';
                        }
                        options.resourceLinkOnNewLine = true;
                        options.sharedReportChartMinHeight = 450;
                    }
                    if (useSubSourceColorMap) {
                        options.seriesColorMapKey = 'subSource';
                    }
                    this.chart = new CategoryValueXYChart(
                        series,
                        options,
                        chartMetadata,
                        xySeries
                    );
                }
                break;
            }
            case ReportBlockTypeEnum.SENTIMENT_TREND: {
                this.setColorMapDefaultValues();
                chartSeries = this.reportsChartSeriesConverterService.getChartSeries(series, block);
                chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
                    'block_' + this.blockPosition, key, this.chartDisplayElement
                );
                chartConfiguration.padding = {
                    noPadding: true
                };
                chartConfiguration.header.headlessHeadingTop = 15;
                chartConfiguration.disableLegendScroll = !this.isCanvas;
                chartConfiguration.header.alignSeriesLabelWithDateRange = true;
                chartSettings = this.am5ChartSettingsService.getChartSettings(key, this.chartDisplayElement);
                chartSettings.width = this.chartContentWidth;
                chartSettings.height = imageHeightPx;
                chartSettings.colorMap = this.chartDisplayElement.styles.colorMap;
                chartSettings.currencyCode = this.chartOptions.currencyCode;
                this.am5ChartTooltipService.setDoLink(false);
                chartSeries.forEach((x, i) => {
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip(chartSeries, chartConfiguration, chartSettings, i, this.preview);
                });
                this.am5Chart = new Am5DateValueXYChart(
                    chartSeries,
                    chartConfiguration,
                    chartSettings
                );
                break;
            }
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS: {
                const top10ChartOptions: CategoryValueXYChartOptions = this.reportsChartOptionsService.getTop10ChartOptions(this.chartDisplayElement);
                top10ChartOptions.noPadding = true;
                top10ChartOptions.showFewerItemsAtLimitedSpace = false;
                if (this.chartDisplayElement.metric === ChartMetricsEnum.domainAuthority) {
                    top10ChartOptions.noPercentage = true;
                }
                if (this.block.type.key === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS) {
                    top10ChartOptions.label = this.localizationService.transform('Reports.googleAnalyticsCharts.topSocialReferrals.label');
                    top10ChartOptions.resourceLinkOnNewLine = true;
                }
                if (this.chartDisplayElement.chartHeaderSettings.entityProfile) {
                    this.nonAmChartSeries = series;
                } else {
                    this.chart = new CategoryValueXYChart(
                        series,
                        top10ChartOptions,
                        this.getChartMetadata(),
                        this.reportsChartSeriesConverterService.getChartSeries(series, block)
                    );
                }

                break;
            }
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS: {
                if (this.chartDisplayElement.chartHeaderSettings.entityProfile) {
                    this.nonAmChartSeries = series;
                    break;
                }
                const colorMap = this.chartDisplayElement.styles.colorMap;
                if (this.chartDisplayElement.trackedColorChanged && colorMap['barColor']) {
                    colorMap['seriesColor0'] = colorMap['barColor'];
                }
                chartSeries = this.reportsChartSeriesConverterService.getChartSeries(series, block);
                chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
                    'block_' + this.blockPosition, key, this.chartDisplayElement
                );
                chartConfiguration.padding = {
                    noPadding: true
                };
                chartConfiguration.header.headlessHeadingTop = 15;
                chartConfiguration.disableLegendScroll = !this.isCanvas;
                chartConfiguration.header.alignSeriesLabelWithDateRange = true;
                chartConfiguration.showFewerItemsAtLimitedSpace = false;
                if (this.chartDisplayElement.metric === ChartMetricsEnum.domainAuthority) {
                    (chartConfiguration as Am5CategoryValueXYBaseChartConfiguration).noPercentage = true;
                }
                if (!this.chartDisplayElement.chartAnnotations) {
                    this.chartDisplayElement.chartAnnotations = new ChartAnnotations();
                }
                chartSettings = this.am5ChartSettingsService.getChartSettings(key, this.chartDisplayElement);
                chartSettings.width = this.chartContentWidth;
                chartSettings.height = imageHeightPx;
                chartSettings.colorMap = this.chartDisplayElement.styles.colorMap;
                chartSettings.currencyCode = this.chartOptions.currencyCode;
                this.am5ChartTooltipService.setDoLink(false);
                chartSeries.forEach((x, i) => {
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, this.preview);
                });
                if (this.isBarChart(chartSettings)) {
                    this.am5Chart = new Am5CategoryValueXYBarChart(chartSeries, chartConfiguration, chartSettings) as unknown as Am5Chart;
                } else {
                    this.am5Chart = new Am5CategoryValueXYColumnChart(chartSeries, chartConfiguration, chartSettings) as unknown as Am5Chart;
                }
                this.hasAnnotation = !!chartSettings.chartAnnotations;
                this.setupAnnotationsWhenStable(this.am5Chart);
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                this.setColorMapDefaultValues();
                this.applyAccentColor(accentColor, key);
                chartSeries = this.reportsChartSeriesConverterService.getChartSeries(series, block);
                chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
                    'block_' + this.blockPosition, key, this.chartDisplayElement
                );
                chartConfiguration.padding = {
                    noPadding: true
                };
                chartConfiguration.header.headlessHeadingTop = 15;
                chartConfiguration.header.alignSeriesLabelWithDateRange = true;
                if (!this.chartDisplayElement.chartAnnotations) {
                    this.chartDisplayElement.chartAnnotations = new ChartAnnotations();
                }
                chartSettings = this.am5ChartSettingsService.getChartSettings(key, this.chartDisplayElement) as Am5DateValueXYChartSettings;
                chartSettings.width = this.chartContentWidth;
                chartSettings.height = imageHeightPx;
                chartSettings.colorMap = this.chartDisplayElement.styles.colorMap;
                chartSettings.currencyCode = this.chartOptions.currencyCode;
                chartSettings.isSingleSeries = true;
                this.am5ChartTooltipService.setDoLink(false);
                chartSeries.forEach((x, i) => {
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, this.preview);
                });
                this.am5Chart = new Am5DateValueXYChart(
                    chartSeries,
                    chartConfiguration,
                    chartSettings
                );
                this.hasAnnotation = !!chartSettings.chartAnnotations;
                this.setupAnnotationsWhenStable(this.am5Chart);
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME: {
                let mapChartOptions, colorMap;
                if (this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.MAP) {
                    mapChartOptions = this.reportsChartOptionsService.getMapChartOptions(this.chartDisplayElement);
                    mapChartOptions.styles.initialLatitude = this.chartDisplayElement.styles?.geographyScale === GeographyTypesEnum.world ? -5 : 5;
                    mapChartOptions.legendScrollDisabled = true;
                    mapChartOptions.noPadding = true;
                    colorMap = mapChartOptions.styles.colorMap;
                    if (colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10']) {
                        colorMap['region10'] = colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10'];
                    }
                    colorMap['backgroundColor'] = this.blockDisplayElement.styles.backgroundColor;
                    if (series && series.length && series[0].styles) {
                        if (series[0].styles.regionIsoCode) {
                            mapChartOptions.regionIsoCode = series[0].styles.regionIsoCode + '';
                        }
                        if (series[0].styles.geographyScale) {
                            mapChartOptions.geographyScale = <GeographyTypesEnum>series[0].styles.geographyScale;
                        }
                    }
                    this.chart = new MapChartModel(
                        series,
                        mapChartOptions,
                        this.getChartMetadata()
                    );
                } else {
                    mapChartOptions = this.reportsChartOptionsService.getTop10ChartOptions(this.chartDisplayElement);
                    mapChartOptions.showFewerItemsAtLimitedSpace = false;
                    mapChartOptions.noPadding = true;
                    colorMap = mapChartOptions.styles.colorMap;
                    if (colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10']) {
                        colorMap['region10'] = colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10'];
                    }
                    this.chart = new CategoryValueXYChart(
                        series,
                        mapChartOptions,
                        this.getChartMetadata(),
                        this.reportsChartSeriesConverterService.getChartSeries(series, block)
                    );
                }
                break;
            }
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON: {
                this.setColorMapDefaultValues();
                const sentimentBreakdownOptions = this.reportsChartOptionsService.getSentimentBreakDownComparisonOptions(this.chartDisplayElement);
                sentimentBreakdownOptions.noPadding = true;
                sentimentBreakdownOptions.styles['legendItemWidth'] = 110;
                sentimentBreakdownOptions.legendScrollDisabled = true;
                this.chart = new CategoryValueXYChart(
                    series,
                    sentimentBreakdownOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, block)
                );
                break;
            }
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON: {
                this.setColorMapDefaultValues();
                const mediaTypeBreakdownOptions = this.reportsChartOptionsService.getMediaTypeBreakDownComparisonOptions(this.chartDisplayElement);
                mediaTypeBreakdownOptions.noPadding = true;
                mediaTypeBreakdownOptions.styles['legendItemWidth'] = 110;
                mediaTypeBreakdownOptions.legendScrollDisabled = true;
                this.chart = new CategoryValueXYChart(
                    series,
                    mediaTypeBreakdownOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, block)
                );
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND: {
                const coverageImpactTrendOptions = this.reportsChartOptionsService.getCoverageImpactTrendOptions(this.chartDisplayElement);
                coverageImpactTrendOptions.styles['legendItemWidth'] = 150;
                coverageImpactTrendOptions.noPadding = true;
                this.chart = new DateValueXYChart(
                    series,
                    coverageImpactTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            }
            case ReportBlockTypeEnum.REFERRAL_TREND: {
                const referralTrendOptions = this.reportsChartOptionsService.getReferralTrendOptions(this.chartDisplayElement);
                referralTrendOptions.noPadding = true;
                this.chart = new DateValueXYChart(
                    series,
                    referralTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            }
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME: {
                const prominenceTrendOptions = this.reportsChartOptionsService.getProminenceOverTimeChartOptions(this.chartDisplayElement);
                prominenceTrendOptions.prominenceStatus = this.prominenceStatus;
                prominenceTrendOptions.noPadding = true;
                this.chartDisplayElement.styles['prominenceStatus'] = this.prominenceStatus;
                this.chart = new DateValueXYChart(
                    series,
                    prominenceTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.blockDisplayElement.chartBlock)
                );
                break;
            }
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH: {
                const prominenceReachOptions = this.reportsChartOptionsService.getProminencByReachOptions(this.chartDisplayElement);
                prominenceReachOptions.prominenceStatus = this.prominenceStatus;
                prominenceReachOptions.noPadding = true;
                this.chartDisplayElement.styles['prominenceStatus'] = this.prominenceStatus;
                this.chart = new HeatChartModel(
                    series,
                    prominenceReachOptions,
                    this.getChartMetadata()
                );
                break;
            }
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES: {
                let chartSpecificTypeEnum: ChartSpecificTypesEnum;
                let hideValueLabel: boolean;
                let exportLabelTotalKey: string;
                let columnHeightThreshold;
                let categoryLabelVerticalShiftPx;
                switch (this.chartDisplayElement.metric) {
                    case ExternalChartMetricsEnum.sessions:
                    case ExternalChartMetricsEnum.users:
                        chartSpecificTypeEnum = this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.BAR ?
                            ChartSpecificTypesEnum.BAR : ChartSpecificTypesEnum.COLUMN;
                        hideValueLabel = true;
                        categoryLabelVerticalShiftPx = -1;
                        break;
                    case ExternalChartMetricsEnum.sessionsNewReturning:
                    case ExternalChartMetricsEnum.usersNewReturning:
                        chartSpecificTypeEnum = this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.BAR ?
                            ChartSpecificTypesEnum.MULTI_STACKED_BAR : ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
                        hideValueLabel = false;
                        columnHeightThreshold = 13;
                        categoryLabelVerticalShiftPx = -2;
                        break;
                }
                if (this.chartDisplayElement.metric === ExternalChartMetricsEnum.sessions
                    || this.chartDisplayElement.metric === ExternalChartMetricsEnum.sessionsNewReturning) {
                    exportLabelTotalKey = ExternalChartMetricsEnum.sessions;
                } else {
                    exportLabelTotalKey = ExternalChartMetricsEnum.users;
                }
                const topReferralSourcesOptions = this.reportsChartOptionsService.getReferralSourcesOptions(
                    this.chartDisplayElement,
                    this.block.type.key as ReportBlockTypeEnum,
                    chartSpecificTypeEnum
                );
                topReferralSourcesOptions.hideValueLabel = hideValueLabel;
                topReferralSourcesOptions.exportLabelTotalKey = exportLabelTotalKey;
                topReferralSourcesOptions.showFewerItemsAtLimitedSpace = true;
                topReferralSourcesOptions.noPadding = true;
                if (columnHeightThreshold) {
                    topReferralSourcesOptions.styles.columnHeightThreshold = columnHeightThreshold;
                }
                topReferralSourcesOptions.styles.categoryLabelVerticalShiftPx = categoryLabelVerticalShiftPx;
                topReferralSourcesOptions.styles.legendItemWidth = 120;
                const metaData = this.getChartMetadata();
                if (this.chartDisplayElement?.metric) {
                    let metricLabel = this.chartDisplayElement.metric.split('_')[0];
                    metricLabel = '[bold]' + metricLabel.charAt(0) + metricLabel.slice(1).toLowerCase();
                    metaData.secondarySeriesLabelText = metaData.primarySeriesLabelText;
                    metaData.secondarySeriesUrl = metaData.primarySeriesUrl;
                    metaData.primarySeriesLabelText = metricLabel;
                    metaData.primarySeriesUrl = '';
                }

                this.chart = new CategoryValueXYChart(
                    series,
                    topReferralSourcesOptions,
                    metaData,
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            }
            case ReportBlockTypeEnum.TOP_TERMS: {
                if (this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.CARD_LIST) {
                    this.nonAmChartSeries = series || [];
                    this.chartOptions = this.reportsChartOptionsService.getTopTermsOptions(this.chartDisplayElement);
                } else if (this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
                    const wordCloudOptions = this.reportsChartOptionsService.getTopTermsOptions(this.chartDisplayElement);
                    wordCloudOptions.noPadding = true;
                    wordCloudOptions.disableViewMentionLink = true;
                    this.chart = new WordCloudChartModel(
                        series as WordCloudSeriesModel[],
                        wordCloudOptions,
                        this.getChartMetadata()
                    );
                }
                this.updateTwigData();
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_METRIC: {
                if ((heightChanged || widthChanged) && this.block.displayElement.displayType !== BlockDisplayTypeEnum.ONLINE) {
                    // clone the series for the case we change height or padding, otherwise, it would not re-generate image as no data point change
                    this.nonAmChartSeries = series.map(item => item?.clone());
                } else {
                    this.nonAmChartSeries = series;
                }
                break;
            }
            default:
                this.chart = null;
                break;
        }

        this.loading = this.block.displayElement.displayType !== BlockDisplayTypeEnum.ONLINE;
        this.reportSeries = series;
        this.changeDetectorRef.detectChanges();
        if (this.loading) {
            requestAnimationFrame(() => {
                this.changeDetectorRef.detectChanges();
            });
        }
    }

    private hasDataArrived(series: ReportSeriesModel[]): boolean {
        return series.some(s => {
            try {
                return s.dataProvider?.getValue() !== null;
            } catch {
                return true;
            }
        });
    }

    private waitForData(series: ReportSeriesModel[]): void {
        this.dataArrivalSubscription = merge(...series.map(s => s.dataProvider)).pipe(
            filter(data => data !== null),
            take(1)
        ).subscribe({
            next: () => this.rebuildChart(),
            error: () => this.rebuildChart()
        });
    }

    private rebuildChart(): void {
        this.dataRefreshed = true;
        this.updateChart();
    }

    isNonAmChartGenerate(type: ReportBlockTypeEnum): boolean {
        return (type === ReportBlockTypeEnum.COVERAGE_SUMMARY ||
            (type === ReportBlockTypeEnum.TOP_TERMS && this.chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.CARD_LIST)
        || type === ReportBlockTypeEnum.COVERAGE_METRIC);
    }

    async generateImage(type: ReportBlockTypeEnum, image?: any) {
        this.imageString = image;
        if (this.isNonAmChartGenerate(type)) {
            this.loading = true;
            try {
                const imageString = await this.exportNonAm4Chart(type, this.blockDisplayElement.chart.refreshed);
                this.imageString = imageString;
            } catch (e) {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
                return;
            }
        }

        this.updateTwigData();
        this.loading = false;
        this.changeDetectorRef.detectChanges();
    }

    sourceDeleted(deleted: boolean): void {
        if (deleted) {
            this.loading = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    override updateTwigData() {
        const data: any = {};
        this.twigData = Object.assign(data, this.block.displayElement, this.block.displayElement.settings);

        const blockDisplayElement = this.block.displayElement as BriefingChartDisplayElementModel;

        if (this.imageString) {
            blockDisplayElement.chart.chartUrl = this.imageString;
        }

        // FOR NOW UNTIL WE CHANGE RENDER SERVICE TO JUST USE chartUrl
        this.twigData.url = blockDisplayElement.chart.chartUrl;
        this.twigData.chartUrl = blockDisplayElement.chart.chartUrl;
        this.twigData.width = blockDisplayElement.chart.widthPx;
        this.twigData.displaySize = blockDisplayElement.chart.heightPx;
        if (this.isCanvas) {
            this.twigData.widthPercent = this.blockDisplayElement.chart.widthPercent;
        }

        if (this.blockDisplayElement.chartBlock.type.key === ReportBlockTypeEnum.TOP_TERMS) {
            this.twigData.accentColor = this.barColor;
            if (this.isCanvas) {
                this.applyAccentColor(this.blockDisplayElement.styles['accentColor'], ReportBlockTypeEnum.TOP_TERMS);
            }
        }

        this.loading = false;
        this.changeDetectorRef.detectChanges();
    }

    async loadFonts() {
        const { fonts } = await import('assets/fonts/fonts');
        return fonts;
    }

    private async exportNonAm4Chart(type: ReportBlockTypeEnum, refreshed: boolean): Promise<string> {
        let node: any;

        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_SUMMARY: {
                node = this.coverageSummaryChart?.nativeElement?.parentNode?.children[0];
                break;
            }
            case ReportBlockTypeEnum.TOP_TERMS: {
                node = this.topTermChart?.nativeElement?.parentNode?.children[0];
                break;
            }
            case ReportBlockTypeEnum.COVERAGE_METRIC: {
                node = this.coverageMetricChart?.nativeElement?.parentNode?.children[0];
                break;
            }
        }

        if (!node) {
            return;
        }

        const displayElement = (this.block.displayElement as BriefingChartDisplayElementModel);
        let paddingLeft = displayElement?.styles?.padding?.paddingAll || 0;
        let paddingRight = displayElement?.styles?.padding?.paddingAll || 0;
        if (displayElement?.styles?.padding?.paddingOption) {
            paddingLeft = displayElement?.styles?.padding?.leftPadding || 0;
            paddingRight = displayElement?.styles?.padding?.rightPadding || 0;
        }

        let width = (this.pageBuilderService.getColumnWidth(this.blockPosition) - paddingLeft - paddingRight) *
            displayElement.chart.widthPercent / 100 || null;

        displayElement.chart.widthPx = width;
        const oldChartUrl = displayElement.chart.chartUrl || displayElement.chart.oldChartUrl || null;
        let height = displayElement.chart.heightPx || null;
        let oldWidth = displayElement.chart.oldWidthPx || null;
        let oldHeight = displayElement.chart.oldHeightPx || null;

        if (width) {
            width = Math.floor(width);
        }
        if (height) {
            height = Math.floor(height);
        }
        if (oldWidth) {
            oldWidth = Math.floor(oldWidth);
        }
        if (oldHeight) {
            oldHeight = Math.floor(oldHeight);
        }

        if ((oldWidth !== width) || (oldHeight !== height) || displayElement.chart.generateOnSend || refreshed ||
            !displayElement.chartBlock.equals(displayElement.oldChartBlock)) {
            this.blockDisplayElement.chart.oldWidthPx = width;
            this.blockDisplayElement.chart.oldHeightPx = height;

            const fonts = await this.loadFonts();

            const options = {
                skipFonts: false,
                width: width,
                height: height,
                canvasWidth: width,
                canvasHeight: height,
                fontEmbedCSS: fonts,
                // quality: 0.9 // only work with jpeg
            };

            const imageString = await htmlToImage.toPng(node, options);
            // const imageString = await htmlToImage.toJpeg(node, options);
            if ((this.block?.displayElement as BriefingChartDisplayElementModel)?.chart) {
                (this.block.displayElement as BriefingChartDisplayElementModel).chart.refreshed = false;
            }

            return imageString;
        } else {
            return oldChartUrl || '';
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public chartExport(event: ChartExportEventModel) {
        this.chart.export(event.eventKey.toLowerCase() as keyof am4core.IExportOptions, this.chartDisplayElement.title);
    }

    /**
     * Cache the labels so that we don't have to call a function in the template
     */
    protected setLabels(): void {
        if (this.chartDisplayElement?.series?.[0]?.dateRange
            // For shared report, we already set its relative and absolute date rage when mapping api blocks
            // For date range "ALL" scenario, we cannot do localization in model, so we keep it here
            && (!this.chartDisplayElement.series[0].isSharedReport || this.chartDisplayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.chartDisplayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(
                this.chartDisplayElement.series, (this.block?.displayElement as BriefingChartDisplayElementModel).chartBlock.type.key as ReportBlockTypeEnum
            );
        }
    }

    /**
     * Performs an action on the block
     *
     * @param action A key for a specific action
     */
    onAction(action: string, event?: any): void {
        if (event instanceof KeyboardEvent &&
            event.key !== ActionKeys.ENTER &&
            event.key !== ActionKeys.SPACE
        ) {
            return;
        }

        if (!this.block.actionable) {
            return;
        }

        switch (action) {
            case 'chart':
                this.pageBuilderService.selectBlock(this.block);
                this.pageBuilderService.updateChart(this.block);
                break;
            case 'edit':
                this.pageBuilderService.selectBlock(this.block);
                break;
            case 'delete':
                this.chart?.destroy();
                this.pageBuilderService.removeBlock(this.block, this.blockPosition);
                break;
            case 'duplicate':
                this.pageBuilderService.duplicateBlock(this.block, this.blockPositionBottom);
                break;
            case 'annotate':
                this.enterAnnotationMode();
                break;
        }

        event?.stopPropagation();
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        return this.chart ? this.chart.getExportSeriesLabel() : '';
    }

    setContextMenuEditingOptions(): void {
        super.setContextMenuEditingOptions();
        if (this.block.canEdit) {
            const editOption = this.editingOptions.find(x => x.label === 'PageBuilder.blockMenu.dropdown.edit');
            if (editOption) {
                editOption.label = 'PageBuilder.blockMenu.dropdown.editChart';
                editOption.command = () => this.onAction('chart');
            }
        }
        if (this.block.canAnnotate) {
            const annotationMenuItem = {
                label: 'Reports.chartSetupDialog.annotate',
                command: () => this.onAction('annotate')
            };
            this.editingOptions.splice(this.editingOptions.length - 1, 0, annotationMenuItem);
        }
    }

    public getChartMetadata(): ChartMetadataModel {
        if (!this.chartDisplayElement || !this.chartDisplayElement.series || !this.chartDisplayElement.series[0]) {
            return null;
        }

        const singleSeries = this.chartDisplayElement.series.length === 1;
        const externalSource = this.chartDisplayElement.series.find(series => series.hasValidExternalSource());

        let primarySeriesLabelText, secondarySeriesLabelText, primarySeriesUrl, secondarySeriesUrl;
        if (externalSource) {
            primarySeriesLabelText = this.chartDisplayElement.series[0].label;
            primarySeriesUrl = this.chartDisplayElement.series[0].getSeriesUrl();

            if (!singleSeries) {
                secondarySeriesLabelText = this.chartDisplayElement.series[1].label;
                secondarySeriesUrl = this.chartDisplayElement.series[1].getSeriesUrl();
            }
        } else if (singleSeries) {
            primarySeriesLabelText = this.chartDisplayElement.series[0].label;
            primarySeriesUrl = this.chartDisplayElement.series[0].getSeriesUrl();
        }

        const noContent = this.localizationService.transform('Monitoring.result.noContentFound');
        const noContentLabel = noContent;

        const noDataFromDateRangeLabel = this.localizationService.transform('Reports.chart.noDataFromDateRange');

        return {
            type: (this.block?.displayElement as BriefingChartDisplayElementModel).chartBlock.type.key,
            titleText: this.chartDisplayElement.title,

            // @NOTE - if date range becomes per-series, this will have to change
            dateRange: ReportDateRangeModel.clone(this.chartDisplayElement.series[0].dateRange),
            dateRangeLabelText: this.chartDisplayElement.dateRangeLabel,

            primarySeriesLabelText: primarySeriesLabelText,
            primarySeriesUrl: primarySeriesUrl ? this.appBaseHref + primarySeriesUrl : null,
            secondarySeriesLabelText: secondarySeriesLabelText,
            secondarySeriesUrl: secondarySeriesUrl ? this.appBaseHref + secondarySeriesUrl : null,

            labelDisplayOptions: this.chartDisplayElement.labelDisplayOptions,
            stackedChartTypes: this.chartDisplayElement.stackedChartTypes,
            enableDataDisplaySettings: this.chartDisplayElement.enableDataDisplaySettings,
            noContentLabel: noContentLabel,
            noDataFromDateRangeLabel: noDataFromDateRangeLabel
        };
    }

    setNoProminenceContactLink(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            this.noProminenceContactLink = 'mailto:inquiry@burrelles.com';
        } else {
            this.noProminenceContactLink = 'https://www.agilitypr.com/contact/';
        }
    }

    formatDateRangeLabel(format: string) {
        this.chart.dateRangeLabel.text = this.reportSeriesPreviewService.dateRangeToString(this.chartDisplayElement.series, null, format);
    }

    updateNoData(noData: boolean): void {
        this.noData = noData;
    }

    enterAnnotationMode(): void {
        const chart = this.am5Chart.clone();
        const containerWidth = this.blockContentContainer.nativeElement.clientWidth;
        this.updateConfigurationForAnnotationDialog(chart.config);
        const dialogInjector = Injector.create({
            providers: [{ provide: AbstractPageBuilderService, useValue: this.pageBuilderService }],
            parent: this.injector
        });
        this.coreDialogService.open(
            ChartAnnotationDialogComponent,
            {
                inputs: {
                    chart: chart,
                    reportSeries: this.reportSeries.map(x => x.clone()),
                    displayElement: this.chartDisplayElement,
                    containerWidth: containerWidth,
                    chartContainerWidth: this.isCanvas ?
                        this.getChartContentWidth(containerWidth, this.blockDisplayElement) + 30 : this.getImageWidth(containerWidth, this.blockDisplayElement),
                    chartBlockPadding: 2,
                    plotTop: this.plotTop,
                    plotBottom: this.plotBottom
                },
                outputs:{
                    dialogClose: async (chartAnnotations: ChartAnnotations) => {
                        if (!chartAnnotations) {
                            return;
                        }
                        const previousAnnotations = this.chartDisplayElement.chartAnnotations?.clone();
                        const markerState = chartAnnotations.markerState || this.chartDisplayElement.chartAnnotations.markerState;
                        this.chartDisplayElement.chartAnnotations.markerState = markerState;
                        this.chartDisplayElement.chartAnnotations.indicators = chartAnnotations.indicators;
                        this.hasAnnotation = (markerState as any)?.markers?.length > 0;
                        const annotationsChanged = !previousAnnotations ||
                            !this.chartDisplayElement.chartAnnotations.isEqual(previousAnnotations);
                        this.lastChartDisplayElement = this.chartDisplayElement?.clone();
                        if (this.isCanvas) {
                            await this.amCanvasChart.setChartAnnotations();
                        } else {
                            await this.am5EmailBriefingChart.setChartAnnotations();
                            if (annotationsChanged) {
                                await this.am5EmailBriefingChart.regenerateImageForAnnotationChange();
                            }
                        }
                        this.changeDetectorRef.detectChanges();
                        if ((this.pageBuilderService as BriefingsService).triggerDirtyCheck) {
                            (this.pageBuilderService as BriefingsService).triggerDirtyCheck();
                        }
                    }
                }
            },
            dialogInjector
        );
    }

    setPlotPosition(overrideWidth?: number, overrideHeight?: number): void {
        if (!this.am5Chart?.annotator) {
            return;
        }
        const width = overrideWidth ?? this.am5Chart.settings.width;
        const height = overrideHeight ?? this.am5Chart.settings.height;
        this.am5Chart.annotator.setContainerWidth(Math.max(width, 225));
        this.am5Chart.annotator.setContainerHeight(this.isCanvas ? this.chartContentHeight : height);
        switch (this.am5Chart.config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                const plotContainer = (this.am5Chart as Am5DateValueXYChart).amchart.plotContainer;
                this.plotTop = plotContainer.toGlobal({ x: 0, y: 0 }).y;
                this.plotBottom = this.am5Chart.legend.toGlobal({ x: 0, y: 0 }).y;
                this.plotLeft = 0;
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                const barPlotContainer = (this.am5Chart as Am5CategoryValueXYBarChart | Am5CategoryValueXYColumnChart).amchart.plotContainer;
                this.plotTop = barPlotContainer.toGlobal({ x: 0, y: 0 }).y;
                this.plotBottom = height;
                this.plotLeft = 0;
        }
        this.am5Chart.annotator.setPlotTop(this.plotTop + this.annotationTopAdjust);
        this.am5Chart.annotator.setPlotBottom(this.plotBottom + this.annotationBottomAdjust);
        this.am5Chart.annotator.setPlotLeft(this.plotLeft);
        this.am5Chart.annotator.setPlotRight(0);
    }

    // the other option would be nested requestAnimationFrame
    private nextFrame(root: am5.Root, timeoutMs: number): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            const timeoutId = setTimeout(() => resolve(false), timeoutMs);
            root.events.once('frameended', () => {
                clearTimeout(timeoutId);
                resolve(true);
            });
        });
    }

    // Resolves once the chart's plot position has stopped changing between frames.
    private async waitForStablePlotPosition(chart: Am5Chart, stableFrames: number = 2, maxFrames: number = 30, idleTimeoutMs: number = 200): Promise<void> {
        let lastPlotBottom: number | null = null;
        let stableCount = 0;
        for (let frame = 0; frame < maxFrames; frame++) {
            // the chart may have been rebuilt by updateChart() while we were waiting
            if (this.am5Chart !== chart || !chart.root) {
                return;
            }
            const gotFrame = await this.nextFrame(chart.root, idleTimeoutMs);
            if (!gotFrame) {
                return;
            }

            this.setPlotPosition();
            stableCount = lastPlotBottom !== null && Math.abs(this.plotBottom - lastPlotBottom) < 0.5 ? stableCount + 1 : 0;
            lastPlotBottom = this.plotBottom;
            if (stableCount >= stableFrames) {
                return;
            }
        }
    }

    public handleChartExportResized = (exportWidth: number, exportHeight: number): void => {
        this.setPlotPosition(exportWidth, exportHeight);
        this.am5Chart.annotator?.applyContainerGeometry();
    };

    public isBarChart(settings: Am5ChartSettings): boolean {
        return settings.chartSpecificType === ChartSpecificTypesEnum.BAR ||
            settings.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }

    updateConfigurationForAnnotationDialog(config: Am5ChartConfiguration): void {
        config.padding.customPadding = this.getCustomPaddingForAnnotationDialog(config.type);
        config.header.hasHeader = false;
        config.hasLegend = false;
        config.disableLegendClick = true;
        switch (config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                (config as Am5DateValueXYChartConfiguration).useScrollbar = false;
                break;
        }
    }

    getCustomPaddingForAnnotationDialog(type: ReportBlockTypeEnum): any {
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            default:
                return {
                    top: -this.inDialogAnnotationTopAdjust,
                    right: 0,
                    bottom: this.inDialogAnnotationBottomAdjust,
                    left: 0
                };
        }
    }

    /**
     * For backward compatibility
     */
    setColorMapDefaultValues(): void {
        let colorTypeKey;
        const colorMap = this.chartDisplayElement.styles.colorMap;
        switch ((this.block?.displayElement as BriefingChartDisplayElementModel).chartBlock.type.key) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                this.chartDisplayElement.metrics.forEach((metric, index) => {
                    if (!colorMap['analysisColor' + (index + 1)]) {
                        const color = this.userChartColors?.['generalColors']?.['color' + (index + 1)];
                        if (color) {
                            colorMap['analysisColor' + (index + 1)] = color;
                        }
                    }
                });
                return;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                colorTypeKey = 'sentiments';
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                colorTypeKey = 'networkSelection';
                break;
        }

        let colorTypes = this.chartDisplayElement[colorTypeKey];
        if (!colorTypes && colorTypeKey === 'networkSelection') {
            colorTypes = Object.values(MediaTypeEnum);
        }
        if (colorTypes?.length > 0 && this.chartDisplayElement.styles) {
            if (!this.chartDisplayElement.styles.colorMap) {
                this.chartDisplayElement.styles.colorMap = {};
            }
            const userPrefColorSetKey = colorTypeKey === 'sentiments' ? 'sentimentColors' : 'mediaTypeColors';
            const sentimentEnumToUserPrefColorKeysMap = new Map<SearchCriteriaQuerySentimentsEnum, string>([
                [SearchCriteriaQuerySentimentsEnum.positive, 'positive'],
                [SearchCriteriaQuerySentimentsEnum.neutral, 'neutral'],
                [SearchCriteriaQuerySentimentsEnum.negative, 'negative'],
                [SearchCriteriaQuerySentimentsEnum.notAvailable, 'notAvailable']
            ]);
            colorTypes.forEach(color => {
                const userPrefColorKey = colorTypeKey === 'sentiments' ?
                    sentimentEnumToUserPrefColorKeysMap.get(color)
                    : mediaTypeColorKeyMap.get(color);

                colorMap[color] = colorMap[color] || this.userChartColors?.[userPrefColorSetKey]?.[userPrefColorKey]
                    || stackedBarColorsConfig[color];
            });
        }
    }

    // to do: we want the same padding calculation for CIB as canvas
    private getImageWidth(columnWidth: number, blockDisplayElement: BriefingChartDisplayElementModel): number {
        const chartPaddingWidth = this.getPaddingAndBorderWidth(blockDisplayElement);

        return (columnWidth - chartPaddingWidth) * (blockDisplayElement.chart.widthPercent || 100) / 100;
    }

    // get chart width with no padding space. Notice we multiply by width percentage first, then remove paddings, which is different from current CIB
    private getChartContentWidth(columnWidth: number, blockDisplayElement: BriefingChartDisplayElementModel): number {
        const width = columnWidth * (blockDisplayElement.chart.widthPercent || 100) / 100;
        const chartPaddingWidth = this.getPaddingAndBorderWidth(blockDisplayElement);

        return width - chartPaddingWidth;
    }

    private getPaddingAndBorderWidth(blockDisplayElement: BriefingChartDisplayElementModel): number {
        let chartPaddingWidth = 0;
        if (blockDisplayElement.styles.padding?.paddingOption) {
            chartPaddingWidth = chartPaddingWidth + (blockDisplayElement.styles.padding.leftPadding || 0)
                + (blockDisplayElement.styles.padding.rightPadding || 0);
        } else if (blockDisplayElement.styles.padding?.paddingOption === false) {
            chartPaddingWidth += (blockDisplayElement.styles.padding.paddingAll * 2 || 0);
        }
        if (blockDisplayElement.styles.doBorder) {
            chartPaddingWidth += (blockDisplayElement.styles.borderWidth || 0) * 2;
        }

        return chartPaddingWidth;
    }

    private applyAccentColor(accentColor: string, type: ReportBlockTypeEnum): void {
        if (this.isCanvas) {
            if (!this.chartDisplayElement.trackedColorChanged) {
                this.chartDisplayElement.styles.colorMap['accentColor'] = accentColor;
                const accentColorMapKeys = reportBlockTypesToChartAccentColorMapkeysMap.get(type);
                if (accentColorMapKeys) {
                    accentColorMapKeys.forEach(x => this.chartDisplayElement.styles.colorMap[x] = accentColor);
                    const accentColorSeries = this.chartDisplayElement.series?.[0];
                    if (accentColorSeries.color) {
                        accentColorSeries.color = accentColor;
                        if (type === ReportBlockTypeEnum.PROMINENCE_BY_REACH) {
                            const dataPoints = accentColorSeries.dataProvider.getValue();
                            if (dataPoints?.length > 0) {
                                dataPoints.forEach(x => x.color = accentColor);
                            }
                        }
                    }
                    switch (type) {
                        case ReportBlockTypeEnum.TOP_TERMS:
                            this.twigData.accentColor = accentColor;
                            break;
                        case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                            this.chartDisplayElement.styles.colorMap['metric0'] = this.chartDisplayElement.styles.colorMap['analysisColor1'];
                            break;
                        case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                        case ReportBlockTypeEnum.TOP_10_OUTLETS:
                            if (this.chartDisplayElement.chartHeaderSettings.entityProfile) {
                                this.twigData.accentColor = accentColor;
                            }
                            break;
                        case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                            const lighterColor = ColorModel.RGBtoHEX(ColorModel.createFromHex(accentColor).applyOpacity(0.2).rgb);
                            this.chartDisplayElement.styles.colorMap['choroplethLowValue'] = '#' + lighterColor;
                            break;
                    }
                }
            } else {
                if (type === ReportBlockTypeEnum.TOP_TERMS) {
                    this.twigData.accentColor = this.barColor;
                }

                if (type === ReportBlockTypeEnum.COVERAGE_METRIC) {
                    this.blockDisplayElement.styles['accentColor'] = this.chartDisplayElement.styles.colorMap['accentColor'];
                }
                if ((type === ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME ||
                    type === ReportBlockTypeEnum.TOP_10_OUTLETS) &&
                    this.chartDisplayElement.chartHeaderSettings.entityProfile
                ) {
                    this.twigData.accentColor = this.barColor;
                }
            }
        }
    }

    // Each chart built by updateChart() gets its own run of this; a run whose chart was replaced does nothing.
    private setupAnnotationsWhenStable(chart: Am5Chart): void {
        chart.annotatorConfigured$.pipe(
            filter(x => !!x),
            switchMap(() => this.waitForStablePlotPosition(chart)),
            filter(() => this.am5Chart === chart && !!chart.annotator),
            switchMap(() => chart.annotator.getMarkerArea()),
            filter(x => !!x && this.am5Chart === chart),
            take(1)
        ).subscribe(area => {
            area.availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);
            if (this.isCanvas) {
                this.amCanvasChart.setChartAnnotations();
            } else {
                this.am5EmailBriefingChart.setChartAnnotations();
            }
        });
    }
}
